import { AxiosError } from "axios";
import { action, autorun, computed, observable, reaction, runInAction } from "mobx";
import { fromPromise, IPromiseBasedObservable, PENDING } from "mobx-utils";

import { isKnownLocale, KnownLocale } from "nvent-web/i18n";
import Api from "nvent-web/services/Api";
import { Auth0, UserMetadata } from "nvent-web/services/Auth0";
import bugsnag from "nvent-web/services/bugsnag";
import * as logger from "nvent-web/services/logger";
import { ProfileUpdate } from "nvent-web/services/resources/ProfileResource";
import { Photo } from "nvent-web/types/Photo";
import { CompanyAddress, Consents, Profile } from "nvent-web/types/Profile";
import { Role } from "nvent-web/types/Role";
import { never } from "nvent-web/utils/promise";

const emptyCompanyAddress: CompanyAddress = {
  streetAddress: "",
  postalCode: "",
  locality: "",
  countryCode: "SE",
};

const initialConsents: Consents = {
  marketing: false,
  shareableProjects: true,
};

export class UserStore implements Profile {
  @observable profilePromise = fromPromise(never());

  @computed
  get isAuthenticated() {
    return this.auth0.isAuthenticated;
  }

  @computed
  get isLoading() {
    return this.auth0.isLoading || this.isProfileLoading;
  }

  @computed
  get isProfileLoading() {
    return this.profilePromise.state === "pending";
  }

  @computed
  get isReady() {
    return !this.isLoading && this.auth0.isAuthenticated && !this.isProfileMissing;
  }

  @computed
  get shouldCreateProfile() {
    return !this.isLoading && this.isAuthenticated && this.isProfileMissing;
  }

  @computed
  get isLogoCreating(): boolean {
    return Boolean(this.newLogoPromise && this.newLogoPromise.state === PENDING);
  }

  @computed
  get isAvatarCreating(): boolean {
    return Boolean(this.newAvatarPromise && this.newAvatarPromise.state === PENDING);
  }

  @observable updatingPromise?: IPromiseBasedObservable<void>;

  @observable isProfileMissing = true;

  @observable auth0Id = "";
  @observable id = "";
  @observable email = "";
  @observable isEmailVerified = false;
  @observable certifiedProNumber = "";

  @observable firstName = "";
  @observable lastName = "";
  @observable phoneNumber = "";
  @observable locale: KnownLocale;

  @computed
  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  @observable companyName = "";
  @observable.struct companyAddress: CompanyAddress = { ...emptyCompanyAddress };
  @observable.struct consents: Consents = { ...initialConsents };

  @observable role = Role.installer;

  @computed
  get isAdmin() {
    return this.role === Role.admin || this.role === Role.superAdmin;
  }

  @computed
  get isSuperAdmin() {
    return this.role === Role.superAdmin;
  }

  @observable logo: Photo | null = null;
  @observable newLogoPromise?: IPromiseBasedObservable<Photo>;
  @observable deleteLogoPromise?: IPromiseBasedObservable<Photo>;

  @observable avatar: Photo | null = null;
  @observable newAvatarPromise?: IPromiseBasedObservable<Photo>;
  @observable deleteAvatarPromise?: IPromiseBasedObservable<Photo>;

  constructor(private auth0: Auth0, private api: Api) {
    const storedLocale = localStorage.getItem("app.locale");
    this.locale = storedLocale && isKnownLocale(storedLocale) ? storedLocale : "en";

    reaction(
      () => this.auth0Id,
      () => {
        this.profilePromise = fromPromise(this.loadProfile());
      },
      { fireImmediately: true }
    );

    autorun(() => {
      if (this.auth0.idTokenPayload) {
        const { sub, email, emailVerified, givenName, familyName, phoneNumber } = this.auth0.idTokenPayload;

        runInAction(() => {
          this.auth0Id = sub;
          this.email = email;
          this.isEmailVerified = emailVerified;
          this.firstName = givenName;
          this.lastName = familyName;
          this.phoneNumber = phoneNumber;
        });
      }
    });

    if (bugsnag) {
      autorun(() => {
        bugsnag!.user = this.auth0Id ? { id: this.auth0Id } : {};
      });
    }

    autorun(() => localStorage.setItem("app.locale", this.locale));
  }

  @computed
  get isProfileUpdating(): boolean {
    return Boolean(this.updatingPromise && this.updatingPromise.state === PENDING);
  }

  updateProfile(profile: ProfileUpdate, metadata?: UserMetadata) {
    this.updatingPromise = fromPromise(update.call(this));
    return this.updatingPromise;

    async function update(this: UserStore) {
      if (metadata) {
        try {
          await this.auth0.updateMetadata(metadata);
        } catch (err) {
          logger.error(err);
        }
      }

      this.setProfile((await this.api.profile.update(profile, this.auth0.idToken)).data);
    }
  }

  scheduleProfileUpdate(profile: ProfileUpdate) {
    localStorage.setItem("registration.profile", JSON.stringify(profile));
  }

  @action.bound
  async setLocale(locale: KnownLocale) {
    this.locale = locale;
    if (this.id) {
      await this.api.profile.setLocale(this.locale);
    }
  }

  @action.bound
  async createLogo(logo: File) {
    this.newLogoPromise = fromPromise(this.api.profile.createLogo(logo).then(({ data }) => data));

    await this.newLogoPromise;
    await this.loadProfile();

    return this.newLogoPromise;
  }

  @action.bound
  async deleteLogo(logoId: number) {
    this.deleteLogoPromise = fromPromise(this.api.profile.deleteLogo(logoId).then(({ data }) => data));

    await this.deleteLogoPromise;
    await this.loadProfile();

    return this.deleteLogoPromise;
  }

  @action.bound
  async createAvatar(avatar: File) {
    this.newAvatarPromise = fromPromise(this.api.profile.createAvatar(avatar).then(({ data }) => data));

    await this.newAvatarPromise;
    await this.loadProfile();

    return this.newAvatarPromise;
  }

  @action.bound
  async deleteAvatar(avatarId: number) {
    this.deleteAvatarPromise = fromPromise(this.api.profile.deleteAvatar(avatarId).then(({ data }) => data));

    await this.deleteAvatarPromise;
    await this.loadProfile();

    return this.deleteAvatarPromise;
  }

  private async loadProfile() {
    if (!this.isAuthenticated) {
      this.setProfile(undefined);
      return;
    }

    const scheduledUpdateJSON = localStorage.getItem("registration.profile");
    const scheduledUpdate = scheduledUpdateJSON ? (JSON.parse(scheduledUpdateJSON) as Profile) : undefined;

    if (scheduledUpdate) {
      await this.updateProfile(scheduledUpdate);
      localStorage.removeItem("registration.profile");
    } else {
      return this.setProfile(await this.getProfile());
    }
  }

  private async getProfile(): Promise<Profile | undefined> {
    try {
      return (await this.api.profile.get()).data;
    } catch (e) {
      const error: AxiosError = e;

      if (error.response && error.response.status === 403) {
        return undefined;
      } else {
        throw error;
      }
    }
  }

  @action
  private setProfile(profile: Profile | undefined) {
    const {
      id = "",
      certifiedProNumber = "",
      role = Role.installer,
      locale = this.locale,
      companyName = "",
      companyAddress = { ...emptyCompanyAddress },
      consents = { ...initialConsents },
      logo = null,
      avatar = null,
    } = profile || {};

    this.isProfileMissing = !profile;
    this.id = id;
    this.certifiedProNumber = certifiedProNumber;
    this.role = role;
    this.locale = locale;
    this.companyName = companyName;
    this.companyAddress = companyAddress;
    this.consents = consents;
    this.logo = logo;
    this.avatar = avatar;
  }
}
